import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  BsBuildings,
  BsCashStack,
  BsPersonVcard,
  BsStars,
  BsTelephoneOutbound,
  BsEnvelope,
  BsTelephone,
  BsGeoAlt,
  BsFolder2Open,
  BsPersonCheck,
  BsPiggyBank,
  BsEye,
  BsDownload,
  BsShieldCheck,
  BsCurrencyDollar,
  BsFileEarmarkText,
} from "react-icons/bs";
import { toast } from "react-toastify";
import WebService from "../../../Services/WebService";
import HelperService from "../../../Services/HelperService";
import BackButton from "../../BackButton/BackButton";
import RmAiEmailModal from "../Modals/RmAiEmailModal";
import RmLogActivityModal from "../Modals/RmLogActivityModal";
import CommonModal from "../../Common/CommonModal";

// Brand Color Palette Constants
const BRAND_GREEN = "#66C15E";
const BRAND_GREEN_LIGHT = "#ebf5ea";
const BRAND_GREEN_HOVER = "#54a84d";
const BRAND_DARK = "#0E0E0D";
const BRAND_BORDER = "#d6ebd4";

const DetailBox = ({
  label,
  value,
  isHighlight = false,
}: {
  label: string;
  value?: React.ReactNode;
  isHighlight?: boolean;
}) => (
  <div
    className="p-3.5 rounded-3 h-100 transition-all"
    style={{
      backgroundColor: isHighlight ? "#f4fcf3" : "#f8faf7",
      border: `1px solid ${isHighlight ? "#c2ebbf" : "#e4efe3"}`,
    }}
  >
    <small
      className="d-block mb-1.5 font-11 fw-bold text-uppercase"
      style={{ color: "#606d5f", letterSpacing: "0.5px" }}
    >
      {label}
    </small>
    <div
      className="font-14 fw-semibold"
      style={{ color: isHighlight ? BRAND_GREEN : BRAND_DARK }}
    >
      {value || "-"}
    </div>
  </div>
);

const RmProfileDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(true);
  const [profileData, setProfileData] = useState<any>(null);
  const [userRole, setUserRole] = useState<"ENTREPRENEUR" | "INVESTOR" | "MENTOR">("ENTREPRENEUR");

  // Action modal states
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);

  // Document preview modal states
  const [showDocModal, setShowDocModal] = useState(false);
  const [docModalUrl, setDocModalUrl] = useState("");
  const [docModalTitle, setDocModalTitle] = useState("");
  const [docFileTitle, setDocFileTitle] = useState("");

  useEffect(() => {
    if (params?.id) {
      fetchProfileDetails();
    }
  }, [params?.id]);

  const fetchProfileDetails = () => {
    setLoader(true);
    const profileId = params?.id;
    const currentPath = window.location.pathname;

    let endpoint = `info/${profileId}/view`;
    if (currentPath.includes("/rm/mentors")) {
      endpoint = `mentor/${profileId}`;
    }

    WebService.getAPI({ action: endpoint, body: null, isShowError: false })
      .then((res: any) => {
        let data = res?.info || res?.data || res;
        setProfileData(data);

        if (currentPath.includes("/rm/mentors") || data?.mentor_user) {
          setUserRole("MENTOR");
        } else if (currentPath.includes("/rm/investors") || data?.investor_business_details) {
          setUserRole("INVESTOR");
        } else {
          setUserRole("ENTREPRENEUR");
        }
        setLoader(false);
      })
      .catch((err: any) => {
        setLoader(false);
        toast.error(err?.response?.data?.message || "Failed to load profile details");
      });
  };

  // Full name derived from users / mentor_user object
  const getFullNameFromUser = () => {
    const userObj = profileData?.users || profileData?.mentor_user || profileData;
    if (userObj?.name) return userObj.name;
    const firstLast = `${userObj?.first_name || ""} ${userObj?.last_name || ""}`.trim();
    if (firstLast) return firstLast;
    return (
      profileData?.company_name ||
      profileData?.entrepreneur_business_details?.company_name ||
      "Platform Member"
    );
  };

  const getUserEmail = () => {
    return profileData?.users?.email || profileData?.mentor_user?.email || profileData?.email || "";
  };

  const getUserMobile = () => {
    const userObj = profileData?.users || profileData?.mentor_user || profileData;
    if (userObj?.mobile_number) {
      return `${userObj?.dial_code || ""}-${userObj?.mobile_number}`;
    }
    return "-";
  };

  const getStatus = () => {
    return profileData?.users?.status || profileData?.status || "ACTIVE";
  };

  const getProfileImage = () => {
    const userObj = profileData?.users || profileData?.mentor_user || profileData;
    return userObj?.profile_image || null;
  };

  const documents = profileData?.documentDetails || profileData?.documents || profileData?.entrepreneur_document_details || [];

  const getEntrepreneurDetails = () => profileData?.entrepreneur_business_details || {};
  const getInvestorDetails = () => profileData?.investor_business_details || {};

  const handlePreviewDoc = (url: string, title: string) => {
    if (!url) {
      toast.info("No file URL available for preview");
      return;
    }
    setDocModalUrl(url);
    setDocModalTitle(title || "Document View");
    setDocFileTitle(title || "Document View");
    setShowDocModal(true);
  };

  return (
    <div
      className="page-content pb-5"
      style={{ minHeight: "calc(100vh - 70px)", backgroundColor: "#f6f8f6" }}
    >
      <Container fluid className="px-4 pt-3">
        {/* Header Card in Brand Styling */}
        <div
          className="bg-white p-4 rounded-4 shadow-sm mb-4"
          style={{ border: `1px solid ${BRAND_BORDER}` }}
        >
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-3">
              <BackButton fallback="/rm/dashboard" />
              {getProfileImage() ? (
                <img
                  src={getProfileImage()}
                  alt="avatar"
                  className="rounded-circle object-fit-cover shadow-xs"
                  style={{ width: 68, height: 68, border: `2px solid ${BRAND_GREEN}` }}
                />
              ) : (
                <div
                  className="rounded-circle text-white d-flex align-items-center justify-content-center fw-bold font-22 shadow-xs"
                  style={{
                    width: 68,
                    height: 68,
                    backgroundColor: BRAND_GREEN,
                    boxShadow: "0 4px 12px rgba(102, 193, 94, 0.3)",
                  }}
                >
                  {getFullNameFromUser().charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <h3 className="fw-bold mb-0 font-22" style={{ color: BRAND_DARK }}>
                    {getFullNameFromUser()}
                  </h3>
                  <Badge className={HelperService.decideBadgeClass(getStatus())}>
                    {getStatus()}
                  </Badge>
                  <Badge
                    pill
                    style={{
                      backgroundColor: BRAND_GREEN_LIGHT,
                      color: "#35772f",
                      border: `1px solid ${BRAND_BORDER}`,
                      fontWeight: 600,
                    }}
                    className="px-3 py-1.5 font-12"
                  >
                    {userRole}
                  </Badge>
                </div>
                <div className="d-flex align-items-center gap-3 text-muted font-13 mt-2 flex-wrap">
                  <span className="d-flex align-items-center gap-1.5">
                    <BsEnvelope style={{ color: BRAND_GREEN }} /> {getUserEmail() || "No Email Provided"}
                  </span>
                  <span className="d-flex align-items-center gap-1.5">
                    <BsTelephone style={{ color: BRAND_GREEN }} /> {getUserMobile()}
                  </span>
                  <span className="d-flex align-items-center gap-1.5">
                    <BsGeoAlt style={{ color: "#d9534f" }} />{" "}
                    {getEntrepreneurDetails()?.country ||
                      getInvestorDetails()?.country ||
                      profileData?.country ||
                      "India"}
                  </span>
                </div>
              </div>
            </div>

            {/* Managed RM Action Toolbar in Brand Theme */}
            <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
              <Button
                style={{
                  backgroundColor: BRAND_GREEN,
                  borderColor: BRAND_GREEN,
                  color: "#ffffff",
                  boxShadow: "0 3px 10px rgba(102, 193, 94, 0.25)",
                }}
                className="rounded-pill font-13 fw-semibold px-3.5 py-2 d-flex align-items-center gap-1.5"
                onClick={() => setShowEmailModal(true)}
              >
                <BsStars className="text-warning" /> Compose AI Email
              </Button>
              <Button
                style={{
                  backgroundColor: "#ffffff",
                  borderColor: BRAND_GREEN,
                  color: BRAND_GREEN,
                }}
                className="rounded-pill font-13 fw-semibold px-3.5 py-2 d-flex align-items-center gap-1.5"
                onClick={() => {
                  setShowLogModal(true);
                }}
              >
                <BsTelephoneOutbound /> Log Call
              </Button>
            </div>
          </div>
        </div>

        {loader ? (
          <div
            className="text-center py-5 bg-white rounded-4 shadow-sm"
            style={{ border: `1px solid ${BRAND_BORDER}` }}
          >
            <Spinner animation="border" style={{ color: BRAND_GREEN }} />
            <p className="text-muted font-14 mt-3">Loading full relation profile details...</p>
          </div>
        ) : (
          <Row className="g-4">
            {/* Single-Page Scrollable Content Column */}
            <Col lg={8}>
              {/* Section 1: Basic Account Information */}
              <Card
                className="border-0 shadow-sm rounded-4 mb-4"
                style={{ overflow: "hidden" }}
              >
                <Card.Header
                  className="bg-white py-3"
                  style={{
                    borderBottom: `1px solid ${BRAND_BORDER}`,
                    borderLeft: `4px solid ${BRAND_GREEN}`,
                  }}
                >
                  <h6 className="fw-bold mb-0 d-flex align-items-center gap-2" style={{ color: BRAND_DARK }}>
                    <BsPersonCheck style={{ color: BRAND_GREEN, fontSize: 18 }} /> Basic Account Details
                  </h6>
                </Card.Header>
                <Card.Body className="p-4">
                  <Row className="g-3">
                    <Col md={6}>
                      <DetailBox label="Full Name (from Users)" value={getFullNameFromUser()} />
                    </Col>
                    <Col md={6}>
                      <DetailBox label="Official Email Address" value={getUserEmail()} />
                    </Col>
                    <Col md={6}>
                      <DetailBox label="Mobile Contact" value={getUserMobile()} />
                    </Col>
                    <Col md={6}>
                      <DetailBox
                        label="Preferred Currency"
                        value={
                          profileData?.users?.preferred_currency ||
                          profileData?.preferred_currency ||
                          "INR (₹)"
                        }
                      />
                    </Col>
                    <Col md={6}>
                      <DetailBox
                        label="Location / Country"
                        value={
                          getEntrepreneurDetails()?.country ||
                          getInvestorDetails()?.country ||
                          profileData?.country ||
                          profileData?.users?.country ||
                          "India"
                        }
                      />
                    </Col>
                    <Col md={6}>
                      <DetailBox
                        label="Account Created Date"
                        value={
                          profileData?.users?.created_at || profileData?.created_at
                            ? HelperService.formatDate(profileData?.users?.created_at || profileData?.created_at)
                            : "-"
                        }
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Section 2: Business & Profile Overview */}
              {userRole === "ENTREPRENEUR" && (
                <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ overflow: "hidden" }}>
                  <Card.Header
                    className="bg-white py-3"
                    style={{
                      borderBottom: `1px solid ${BRAND_BORDER}`,
                      borderLeft: `4px solid ${BRAND_GREEN}`,
                    }}
                  >
                    <h6 className="fw-bold mb-0 d-flex align-items-center gap-2" style={{ color: BRAND_DARK }}>
                      <BsBuildings style={{ color: BRAND_GREEN, fontSize: 18 }} /> Business Overview & Entity Details
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <DetailBox
                          label="Company Name"
                          value={
                            getEntrepreneurDetails()?.company_name ||
                            profileData?.company_name ||
                            "-"
                          }
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Industry / Sector"
                          value={
                            getEntrepreneurDetails()?.industry_name ||
                            getEntrepreneurDetails()?.industry ||
                            "-"
                          }
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Funding Goal / Target Amount"
                          value={
                            getEntrepreneurDetails()?.investment_amount ||
                            getEntrepreneurDetails()?.funding_goal_amount ||
                            "-"
                          }
                          isHighlight
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Business Entity Type"
                          value={
                            getEntrepreneurDetails()?.business_entity_type ||
                            "Private Limited"
                          }
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Founding Year"
                          value={getEntrepreneurDetails()?.founding_year || "-"}
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Current Annual Sales"
                          value={getEntrepreneurDetails()?.current_annual_revenue || "-"}
                        />
                      </Col>
                    </Row>

                    <div
                      className="p-3.5 rounded-3"
                      style={{
                        backgroundColor: BRAND_GREEN_LIGHT,
                        border: `1px solid ${BRAND_BORDER}`,
                      }}
                    >
                      <small
                        className="d-block mb-1.5 font-11 fw-bold text-uppercase"
                        style={{ color: "#3d7e36" }}
                      >
                        Business Overview & Strategic Vision
                      </small>
                      <p className="font-14 mb-0" style={{ color: BRAND_DARK, lineHeight: "1.6" }}>
                        {getEntrepreneurDetails()?.business_description ||
                          getEntrepreneurDetails()?.description ||
                          "No overview description provided."}
                      </p>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {userRole === "INVESTOR" && (
                <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ overflow: "hidden" }}>
                  <Card.Header
                    className="bg-white py-3"
                    style={{
                      borderBottom: `1px solid ${BRAND_BORDER}`,
                      borderLeft: `4px solid ${BRAND_GREEN}`,
                    }}
                  >
                    <h6 className="fw-bold mb-0 d-flex align-items-center gap-2" style={{ color: BRAND_DARK }}>
                      <BsCashStack style={{ color: BRAND_GREEN, fontSize: 18 }} /> Investor Entity & Criteria
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <DetailBox
                          label="Investment Firm / Entity Name"
                          value={
                            getInvestorDetails()?.company_name ||
                            getInvestorDetails()?.firm_name ||
                            "Angel / Individual Investor"
                          }
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Investment Ticket Size"
                          value={
                            getInvestorDetails()?.ticket_size ||
                            getInvestorDetails()?.investment_amount ||
                            "-"
                          }
                          isHighlight
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Preferred Investment Sectors"
                          value={
                            getInvestorDetails()?.preferred_sectors ||
                            getInvestorDetails()?.industry_name ||
                            "Growth Sectors"
                          }
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Investment Experience"
                          value={getInvestorDetails()?.investment_experience || "5+ Years"}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {userRole === "MENTOR" && (
                <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ overflow: "hidden" }}>
                  <Card.Header
                    className="bg-white py-3"
                    style={{
                      borderBottom: `1px solid ${BRAND_BORDER}`,
                      borderLeft: `4px solid ${BRAND_GREEN}`,
                    }}
                  >
                    <h6 className="fw-bold mb-0 d-flex align-items-center gap-2" style={{ color: BRAND_DARK }}>
                      <BsPersonVcard style={{ color: BRAND_GREEN, fontSize: 18 }} /> Expertise, Advisory Fees & Bio
                    </h6>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <DetailBox
                          label="Domain Expertise"
                          value={
                            profileData?.expertise ||
                            profileData?.designation ||
                            profileData?.domain ||
                            "-"
                          }
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Session Fee / Advisory Charges"
                          value={profileData?.session_charges ? `₹${profileData.session_charges} / session` : "Free Advisory"}
                          isHighlight
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Professional Experience"
                          value={profileData?.professional_experience || "-"}
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Education Details"
                          value={profileData?.education_details || "-"}
                        />
                      </Col>
                    </Row>

                    <div
                      className="p-3.5 rounded-3"
                      style={{
                        backgroundColor: BRAND_GREEN_LIGHT,
                        border: `1px solid ${BRAND_BORDER}`,
                      }}
                    >
                      <small
                        className="d-block mb-1.5 font-11 fw-bold text-uppercase"
                        style={{ color: "#3d7e36" }}
                      >
                        Biography & Background
                      </small>
                      <p className="font-14 mb-0" style={{ color: BRAND_DARK, lineHeight: "1.6" }}>
                        {profileData?.bio || profileData?.about_me || "No mentor biography provided."}
                      </p>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* Section 3: Financial Details in Brand Theme */}
              <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ overflow: "hidden" }}>
                <Card.Header
                  className="bg-white py-3"
                  style={{
                    borderBottom: `1px solid ${BRAND_BORDER}`,
                    borderLeft: `4px solid ${BRAND_GREEN}`,
                  }}
                >
                  <h6 className="fw-bold mb-0 d-flex align-items-center gap-2" style={{ color: BRAND_DARK }}>
                    <BsPiggyBank style={{ color: BRAND_GREEN, fontSize: 18 }} /> Financial & Investment Details
                  </h6>
                </Card.Header>
                <Card.Body className="p-4">
                  {userRole === "ENTREPRENEUR" ? (
                    <Row className="g-3">
                      <Col md={6}>
                        <DetailBox
                          label="Financial Year Range"
                          value={
                            getEntrepreneurDetails()?.financial_from_year
                              ? `${getEntrepreneurDetails()?.financial_from_year} - ${getEntrepreneurDetails()?.financial_to_year || "Present"}`
                              : "-"
                          }
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Generated Revenue"
                          value={getEntrepreneurDetails()?.generated_revenue || "-"}
                          isHighlight
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Projected Revenue"
                          value={getEntrepreneurDetails()?.projected_revenue || "-"}
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Annual Sales / Revenue"
                          value={
                            getEntrepreneurDetails()?.current_annual_revenue ||
                            getEntrepreneurDetails()?.revenue_of_business ||
                            getEntrepreneurDetails()?.last_year_sale ||
                            "-"
                          }
                          isHighlight
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="EBITDA / Operating Margin"
                          value={getEntrepreneurDetails()?.ebitda || "-"}
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Tangible & Intangible Assets"
                          value={getEntrepreneurDetails()?.tangible_intangible_assets || "-"}
                        />
                      </Col>
                    </Row>
                  ) : userRole === "INVESTOR" ? (
                    <Row className="g-3">
                      <Col md={6}>
                        <DetailBox
                          label="Financial Year Range"
                          value={
                            getInvestorDetails()?.financial_from_year
                              ? `${getInvestorDetails()?.financial_from_year} - ${getInvestorDetails()?.financial_to_year || "Present"}`
                              : "-"
                          }
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Typical Investment Ticket Range"
                          value={
                            getInvestorDetails()?.ticket_size ||
                            getInvestorDetails()?.typical_investment_range ||
                            getInvestorDetails()?.investment_amount ||
                            "-"
                          }
                          isHighlight
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Preferred Investment Age (Years)"
                          value={
                            getInvestorDetails()?.investment_age
                              ? `${getInvestorDetails()?.investment_age} Yrs`
                              : getInvestorDetails()?.investment_experience || "-"
                          }
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Level of Involvement"
                          value={getInvestorDetails()?.level_of_involvement || "Active Advisory / Board Member"}
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Investment Instruments"
                          value={getInvestorDetails()?.investment_instruments || "Equity / Convertible Debt"}
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Open to Syndicates / Co-Investment"
                          value={getInvestorDetails()?.open_syndicates || "Yes"}
                        />
                      </Col>
                    </Row>
                  ) : (
                    <Row className="g-3">
                      <Col md={6}>
                        <DetailBox
                          label="Session Fee / Advisory Charges"
                          value={profileData?.session_charges ? `₹${profileData.session_charges} / session` : "Free Advisory"}
                          isHighlight
                        />
                      </Col>
                      <Col md={6}>
                        <DetailBox
                          label="Preferred Currency"
                          value={
                            profileData?.users?.preferred_currency ||
                            profileData?.preferred_currency ||
                            "INR (₹)"
                          }
                        />
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>

              {/* Section 4: Document Details Section with Preview Modal */}
              <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ overflow: "hidden" }}>
                <Card.Header
                  className="bg-white py-3 d-flex align-items-center justify-content-between"
                  style={{
                    borderBottom: `1px solid ${BRAND_BORDER}`,
                    borderLeft: `4px solid ${BRAND_GREEN}`,
                  }}
                >
                  <h6 className="fw-bold mb-0 d-flex align-items-center gap-2" style={{ color: BRAND_DARK }}>
                    <BsFolder2Open style={{ color: BRAND_GREEN, fontSize: 18 }} /> Uploaded Document Details
                  </h6>
                  <Badge
                    pill
                    style={{ backgroundColor: BRAND_GREEN, color: "#ffffff" }}
                    className="px-2.5 py-1 font-12"
                  >
                    {documents.length} Files
                  </Badge>
                </Card.Header>
                <Card.Body className="p-0">
                  {documents && documents.length > 0 ? (
                    <Table hover responsive className="align-middle mb-0">
                      <thead style={{ backgroundColor: BRAND_GREEN_LIGHT }}>
                        <tr>
                          <th className="py-3 px-4 font-13 fw-bold text-uppercase" style={{ color: BRAND_DARK }}>#</th>
                          <th className="py-3 font-13 fw-bold text-uppercase" style={{ color: BRAND_DARK }}>Document Title</th>
                          <th className="py-3 font-13 fw-bold text-uppercase" style={{ color: BRAND_DARK }}>Type</th>
                          <th className="py-3 font-13 fw-bold text-uppercase text-center" style={{ color: BRAND_DARK }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map((doc: any, idx: number) => {
                          const fileUrl = doc.file_path || doc.url || doc.document || doc.path || "";
                          const fileTitle = doc.document_name || doc.title || doc.name || `Document #${idx + 1}`;
                          const docType = doc.document_type || doc.type || "PDF / Media";

                          return (
                            <tr key={idx}>
                              <td className="px-4 font-13 fw-semibold text-muted">{idx + 1}</td>
                              <td className="fw-bold font-14" style={{ color: BRAND_DARK }}>
                                <div className="d-flex align-items-center gap-2">
                                  <BsFileEarmarkText style={{ color: BRAND_GREEN }} />
                                  <span>{fileTitle}</span>
                                </div>
                              </td>
                              <td>
                                <Badge
                                  style={{
                                    backgroundColor: "#f0f4f0",
                                    color: "#386b33",
                                    border: `1px solid ${BRAND_BORDER}`,
                                  }}
                                  className="font-11 px-2.5 py-1"
                                >
                                  {docType}
                                </Badge>
                              </td>
                              <td className="text-center">
                                {fileUrl ? (
                                  <div className="d-flex align-items-center justify-content-center gap-2">
                                    <Button
                                      size="sm"
                                      style={{
                                        backgroundColor: BRAND_GREEN,
                                        borderColor: BRAND_GREEN,
                                        color: "#ffffff",
                                      }}
                                      className="rounded-pill font-12 px-3 py-1 d-flex align-items-center gap-1"
                                      onClick={() => handlePreviewDoc(fileUrl, fileTitle)}
                                    >
                                      <BsEye /> View Modal
                                    </Button>
                                    <a
                                      href={fileUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="btn btn-sm btn-outline-secondary rounded-pill font-12 px-2.5 py-1"
                                      title="Open in new tab"
                                    >
                                      <BsDownload />
                                    </a>
                                  </div>
                                ) : (
                                  <span className="text-muted font-12">No File Attached</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-center py-4 text-muted font-14">
                      No document files uploaded for this profile yet.
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Sidebar Summary Card in Brand Theme */}
            <Col lg={4}>
              <Card
                className="border-0 shadow-sm rounded-4 mb-4 bg-white"
                style={{ borderTop: `4px solid ${BRAND_GREEN}`, overflow: "hidden" }}
              >
                <Card.Body className="p-4 text-center">
                  {getProfileImage() ? (
                    <img
                      src={getProfileImage()}
                      alt="avatar"
                      className="rounded-circle object-fit-cover shadow-xs mb-3"
                      style={{ width: 84, height: 84, border: `3px solid ${BRAND_GREEN}` }}
                    />
                  ) : (
                    <div
                      className="rounded-circle text-white d-inline-flex align-items-center justify-content-center fw-bold font-24 shadow-xs mb-3"
                      style={{
                        width: 84,
                        height: 84,
                        backgroundColor: BRAND_GREEN,
                        boxShadow: "0 4px 14px rgba(102, 193, 94, 0.35)",
                      }}
                    >
                      {getFullNameFromUser().charAt(0).toUpperCase()}
                    </div>
                  )}

                  <h5 className="fw-bold mb-1" style={{ color: BRAND_DARK }}>
                    {getFullNameFromUser()}
                  </h5>
                  <p className="text-muted font-13 mb-3">{getUserEmail()}</p>
                  <Badge
                    pill
                    style={{ backgroundColor: BRAND_GREEN_LIGHT, color: "#35772f", border: `1px solid ${BRAND_BORDER}` }}
                    className="px-3 py-1.5 font-12 mb-3"
                  >
                    RM Dedicated Profile
                  </Badge>

                  <div className="d-grid gap-2 mt-3">
                    <Button
                      style={{
                        backgroundColor: BRAND_GREEN,
                        borderColor: BRAND_GREEN,
                        color: "#ffffff",
                      }}
                      className="rounded-pill font-13 fw-semibold d-flex align-items-center justify-content-center gap-2 py-2"
                      onClick={() => setShowEmailModal(true)}
                    >
                      <BsStars className="text-warning" /> Send AI Email Draft
                    </Button>

                    <Button
                      variant="outline-secondary"
                      className="rounded-pill font-13 fw-semibold d-flex align-items-center justify-content-center gap-2 py-2"
                      onClick={() => {
                        setShowLogModal(true);
                      }}
                    >
                      <BsTelephoneOutbound /> Log Phone Discussion
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              {/* Status Info Card */}
              <Card
                className="border-0 shadow-sm rounded-4 p-3.5 bg-white mb-4"
                style={{ border: `1px solid ${BRAND_BORDER}` }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="fw-bold font-14" style={{ color: BRAND_DARK }}>
                    Relation Status
                  </span>
                  <Badge className={HelperService.decideBadgeClass(getStatus())}>
                    {getStatus()}
                  </Badge>
                </div>
                <small className="text-muted font-12 d-block" style={{ lineHeight: "1.5" }}>
                  All account parameters, business metrics, financial details, and uploaded documents are presented in a unified view using Prime Shark brand design.
                </small>
              </Card>
            </Col>
          </Row>
        )}
      </Container>

      {/* Action Modals */}
      <RmAiEmailModal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        recipientEmail={getUserEmail()}
        recipientName={getFullNameFromUser()}
        userType={userRole}
        userId={params?.id}
      />

      <RmLogActivityModal
        show={showLogModal}
        onHide={() => setShowLogModal(false)}
        contactName={getFullNameFromUser()}
        contactEmail={getUserEmail()}
        userType={userRole}
      />

      {/* Document Preview Modal */}
      <CommonModal
        show={showDocModal}
        handleClose={() => setShowDocModal(false)}
        title={docModalTitle}
        url={docModalUrl}
        fileTitle={docFileTitle}
      />
    </div>
  );
};

export default RmProfileDetails;

